import { Component, OnInit, ViewChildren, ElementRef, QueryList, ChangeDetectionStrategy } from "@angular/core";
import { FileUploader } from "ng2-file-upload";
import { CreateTicketBaseComponent } from "@mypxplat/xplat/features";
import { UserService, WindowService, SupportService, HelperService } from "@mypxplat/xplat/core";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslateService } from "@ngx-translate/core";
import { NgxSpinnerService } from "ngx-spinner";
import { AppService } from "@mypxplat/xplat/web/core";
import { Router } from "@angular/router";
import { take } from "rxjs/operators";

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-create-ticket",
  templateUrl: "create-ticket.component.html",
  standalone: false,
})
export class CreateTicketComponent extends CreateTicketBaseComponent implements OnInit {
  public searchingCountries: boolean = false;
  public filteredCountries: Array<any>;
  public searchingProducts: boolean = false;
  public filteredProducts: Array<any>;
  public searchingOperatingSystems: boolean = false;
  public filteredOperatingSystems: Array<any>;
  public searchingAudioSoftware: boolean = false;
  public filteredAudioSoftware: Array<any>;
  public ticketResult: any;
  public hasBaseDropZoneOver = false;
  public uploader: FileUploader;
  public uploadingComplete: boolean = false;
  public uploadingFile: any;
  public fileResponse: any;
  public isUploading: boolean = false;
  public uploadProgress: number = 0;

  public attachments: Array<any> = [];

  private _allowedFileTypes = {
    har: true,
    json: true,
    "3g2": true,
    "3gp": true,
    "7z": true,
    aac: true,
    amr: true,
    avi: true,
    bmp: true,
    csv: true,
    doc: true,
    docx: true,
    eml: true,
    gif: true,
    heic: true,
    heif: true,
    ics: true,
    jfif: true,
    jpeg: true,
    jpg: true,
    key: true,
    log: true,
    m4a: true,
    m4v: true,
    mov: true,
    mp3: true,
    mp4: true,
    mp4a: true,
    mpeg: true,
    mpg: true,
    mpga: true,
    neon: true,
    numbers: true,
    odt: true,
    oga: true,
    ogg: true,
    ogv: true,
    opus: true,
    pages: true,
    pdf: true,
    png: true,
    pps: true,
    ppsx: true,
    ppt: true,
    pptx: true,
    qt: true,
    svg: true,
    tif: true,
    tiff: true,
    txt: true,
    vcf: true,
    wav: true,
    webm: true,
    webp: true,
    wmv: true,
    xls: true,
    xlsx: true,
    xml: true,
    yaml: true,
    yml: true,
    zip: true,
    song: true,
    nfo: true,
    spx: true,
    ips: true,
    crash: true,
    dmp: true,
    rar: true,
    notion: true,
  };

  public inputDescription: string;

  constructor(
    userService: UserService,
    public activeModal: NgbActiveModal,
    public translate: TranslateService,
    private _spin: NgxSpinnerService,
    public appService: AppService,
    private _win: WindowService,
    public supportService: SupportService,
    private _router: Router,
    private _translate: TranslateService,
    public helperService: HelperService
  ) {
    super(userService, supportService);
  }

  ngOnInit() {
    super.ngOnInit();

    this.setupUploader();

    if (this.supportService.inProgressTicketArgs) {
      this.ticketArgs = {
        subject: this.supportService.inProgressTicketArgs.subject,
        description: this.supportService.inProgressTicketArgs.description,
      };
      this.selectedAudioSoftware = this.supportService.inProgressTicketArgs.selectedAudioSoftware;
      this.selectedCountry = this.supportService.inProgressTicketArgs.selectedCountry;
      this.selectedOperatingSystem = this.supportService.inProgressTicketArgs.selectedOperatingSystem;
      this.selectedProduct = this.supportService.inProgressTicketArgs.selectedProduct;
      this.supportService.inProgressTicketArgs = {};
    }

    if (this.inputDescription) this.ticketArgs.body = this.inputDescription;
  }

  focusInput(type) {
    this.filteredCountries = this.listCountries.filter((i) => true);
    this.filteredProducts = this.listProducts.filter((i) => true);
    this.filteredOperatingSystems = this.listOperatingSystems.filter((i) => true);
    this.filteredAudioSoftware = this.listAudioSoftware.filter((i) => true);
    switch (type) {
      case "country":
        this.searchingCountries = true;
        break;
      case "product":
        this.searchingProducts = true;
        break;
      case "os":
        this.searchingOperatingSystems = true;
        break;
      case "ware":
        this.searchingAudioSoftware = true;
        break;
    }
  }

  setupUploader() {
    // FileUploader is only used for the drop-zone / file-picker UI; the actual
    // upload goes through UserService.uploadZendeskAttachment (S3 staging + Zendesk complete).
    this.uploader = new FileUploader({
      isHTML5: true,
      method: "POST",
      queueLimit: 1,
      autoUpload: false,
      url: "unused://paeapi-zendesk-passthrough",
      disableMultipart: true,
    });

    this.uploader.onWhenAddingFileFailed = (e) => {
      alert("Please only upload a file from the list of accepted formats.");
      return false;
    };

    this.uploader.onAfterAddingFile = (f) => {
      this.uploadingFile = f;
      f.withCredentials = false;
      const extension = f.file.name.split(".").pop().toLowerCase();
      // Zendesk ticket attachments cap at 50 MB: https://developer.zendesk.com/documentation/ticketing/managing-tickets/adding-ticket-attachments-with-the-api/
      if (f.file.size > 50 * 1024 * 1024) {
        alert("Attachments cannot exceed 50 MB.");
      } else if (!this._allowedFileTypes[extension]) {
        alert("This file type is not allowed.");
      } else {
        this._sendZendeskUpload(f);
        return;
      }
      this.uploader.clearQueue();
      this._win.setTimeout(() => {
        this.setupUploader();
      }, 100);
    };
  }

  private _sendZendeskUpload(item: any) {
    const file: File = item._file;
    this.isUploading = true;
    this.uploadProgress = 0;
    this.uploadingComplete = false;
    this.userService.uploadZendeskAttachment(file, file.name).subscribe({
      next: (event) => {
        this.uploadProgress = event.progress;
        if (event.done) {
          this.uploadingComplete = true;
          this.isUploading = false;
          this.fileResponse = event.response;
          if (this.fileResponse && this.fileResponse.attachment) {
            this.attachments.push(this.fileResponse.attachment);
          }
          this.uploader.clearQueue();
          this._win.setTimeout(() => {
            this.setupUploader();
          }, 100);
        }
      },
      error: (err) => {
        this.isUploading = false;
        this.fileResponse = null;
        alert(err?.error?.error || err?.message || "There was an error uploading your attachment. Please try again.");
        this.uploader.clearQueue();
        this._win.setTimeout(() => {
          this.setupUploader();
        }, 100);
      },
    });
  }

  searchCountries(event) {
    switch (event.key) {
      case "Down":
      case "ArrowDown":
        this.selectedCountry = this.getNextOption(this.filteredCountries, this.selectedCountry);
        break;

      case "Up":
      case "ArrowUp":
        this.selectedCountry = this.getPreviousOption(this.filteredCountries, this.selectedCountry);
        break;

      default:
        if (this.selectedCountry) {
          this.filteredCountries = this.listCountries.filter((item) => {
            if (item) {
              return item.toLowerCase().indexOf(this.selectedCountry?.toLowerCase()) !== -1;
            } else {
              return item.toLowerCase().indexOf(this.selectedCountry?.toLowerCase()) !== -1;
            }
          });
          this.updateLiveRegion("country-status", this.filteredCountries.length);
        }
    }
  }

  selectCountry(country) {
    this.selectedCountry = country;
  }

  searchProducts(event) {
    switch (event.key) {
      case "Down":
      case "ArrowDown":
        this.selectedProduct = this.getNextOption(this.filteredProducts, this.selectedProduct);
        break;

      case "Up":
      case "ArrowUp":
        this.selectedProduct = this.getPreviousOption(this.filteredProducts, this.selectedProduct);
        break;

      default:
        if (this.selectedProduct) {
          this.filteredProducts = this.listProducts.filter((item) => {
            if (item) {
              return item.toLowerCase().indexOf(this.selectedProduct?.toLowerCase()) !== -1;
            } else {
              return item.toLowerCase().indexOf(this.selectedProduct?.toLowerCase()) !== -1;
            }
          });
          this.updateLiveRegion("product-status", this.filteredProducts.length);
        }
        break;
    }
  }

  selectProduct(product) {
    this.selectedProduct = product;
  }

  searchOperatingSystems(event) {
    switch (event.key) {
      case "Down":
      case "ArrowDown":
        this.selectedOperatingSystem = this.getNextOption(this.filteredOperatingSystems, this.selectedOperatingSystem);
        break;

      case "Up":
      case "ArrowUp":
        this.selectedOperatingSystem = this.getPreviousOption(this.filteredOperatingSystems, this.selectedOperatingSystem);
        break;

      default:
        if (this.selectedOperatingSystem) {
          this.filteredOperatingSystems = this.listOperatingSystems.filter((item) => {
            if (item) {
              return item.toLowerCase().indexOf(this.selectedOperatingSystem?.toLowerCase()) !== -1;
            } else {
              return item.toLowerCase().indexOf(this.selectedOperatingSystem?.toLowerCase()) !== -1;
            }
          });
          this.updateLiveRegion("os-status", this.filteredOperatingSystems.length);
        }
    }
  }

  selectOperatingSystem(os) {
    this.selectedOperatingSystem = os;
  }

  searchAudioSoftware(event) {
    switch (event.key) {
      case "Down":
      case "ArrowDown":
        this.selectedAudioSoftware = this.getNextOption(this.filteredAudioSoftware, this.selectedAudioSoftware);
        break;

      case "Up":
      case "ArrowUp":
        this.selectedAudioSoftware = this.getPreviousOption(this.filteredAudioSoftware, this.selectedAudioSoftware);
        break;

      default:
        if (this.selectedAudioSoftware) {
          this.filteredAudioSoftware = this.listAudioSoftware.filter((item) => {
            if (item) {
              return item.toLowerCase().indexOf(this.selectedAudioSoftware?.toLowerCase()) !== -1;
            } else {
              return item.toLowerCase().indexOf(this.selectedAudioSoftware?.toLowerCase()) !== -1;
            }
          });
          this.updateLiveRegion("ware-status", this.filteredAudioSoftware.length);
        }
    }
  }

  selectAudioSoftware(ware) {
    this.selectedAudioSoftware = ware;
  }

  getPreviousOption(optionList, currentOption) {
    if (currentOption && currentOption !== optionList[0]) {
      var index = optionList.indexOf(currentOption);
      return optionList[index - 1];
    }
    return optionList.slice(-1);
  }

  getNextOption(optionList, currentOption) {
    if (currentOption && currentOption !== optionList.slice(-1)) {
      var index = optionList.indexOf(currentOption);
      return optionList[index + 1];
    }
    return optionList[0];
  }

  inputBlur(type) {
    this._win.setTimeout(() => {
      if (type == "country") {
        this.searchingCountries = false;
      } else if (type == "product") {
        this.searchingProducts = false;
      } else if (type == "os") {
        this.searchingOperatingSystems = false;
      } else if (type == "ware") {
        this.searchingAudioSoftware = false;
      }
    }, 250);
  }

  submit() {
    if (this.selectedCountry) {
      let selectedCountryObject = this.countryMap[this.selectedCountry];
      if (selectedCountryObject) {
        this.ticketArgs.country = selectedCountryObject;
      } else {
        this._translate.get("support.choose_valid_country").subscribe((lang) => alert(lang));
        return;
      }
    } else {
      this._translate.get("support.choose_valid_country").subscribe((lang) => alert(lang));
      return;
    }

    if (this.selectedProduct) {
      let selectedProductObject = this.productMap[this.selectedProduct];
      if (selectedProductObject) {
        this.ticketArgs.product = selectedProductObject;
      } else {
        this._translate.get("support.choose_valid_product").subscribe((lang) => alert(lang));
        return;
      }
    } else {
      this._translate.get("support.choose_valid_product").subscribe((lang) => alert(lang));
      return;
    }

    if (this.selectedOperatingSystem) {
      let selectedOperatingSystemObject = this.operatingSystemsMap[this.selectedOperatingSystem];
      if (selectedOperatingSystemObject) {
        this.ticketArgs.os = selectedOperatingSystemObject;
      } else {
        this._translate.get("support.choose_valid_os").subscribe((lang) => alert(lang));
        return;
      }
    } else {
      this._translate.get("support.choose_valid_os").subscribe((lang) => alert(lang));
      return;
    }

    if (this.selectedAudioSoftware) {
      let selectedAudioSoftwareObject = this.audioSoftwareMap[this.selectedAudioSoftware];
      if (selectedAudioSoftwareObject) {
        this.ticketArgs.ware = selectedAudioSoftwareObject;
      } else {
        this._translate.get("support.choose_valid_ware").subscribe((lang) => alert(lang));
        return;
      }
    } else {
      this._translate.get("support.choose_valid_ware").subscribe((lang) => alert(lang));
      return;
    }

    let customFields = [
      {
        id: this.osFieldID,
        value: this.ticketArgs.os.value,
      },
      {
        id: this.productFieldID,
        value: this.ticketArgs.product.value,
      },
      {
        id: this.countryFieldID,
        value: this.ticketArgs.country.value,
      },
      {
        id: this.audioSoftFieldID,
        value: this.ticketArgs.ware.value,
      },
    ];
    if (this.ticketArgs.subject && this.ticketArgs.body) {
      let args: any = {
        subject: this.ticketArgs.subject,
        body: this.ticketArgs.body,
        name: this.user.firstName + " " + this.user.lastName,
        custom_fields: customFields,
      };
      if (this.attachments && this.attachments.length) {
        let tokens = [];
        this.attachments.forEach((att) => {
          if (att && att.upload && att.upload.token) tokens.push(att.upload.token);
        });
        args.uploads = tokens;
      }

      this.loading = true;
      this.supportService.submitTicket(args).subscribe(
        (result: any) => {
          this.supportService.openTickets$.pipe(take(1)).subscribe(() => {
            this.loading = false;
            if (result) {
              this.ticketResult = result;
            }
          });
          this.supportService.getOpenTickets(true);
        },
        (error) => {
          this.loading = false;
          if (error == "Your access token is invalid or has expired.") {
            this.activeModal.close("cancel");
          } else {
            alert("There was an error creating your ticket. Please try creating a ticket directly through our support portal.");
            window.open("https://presonus.zendesk.com");
          }
        }
      );
    } else {
      this._translate.get("products.register.enter_required").subscribe((lang) => alert(lang));
    }
  }

  cancel() {
    if (!this.ticketResult && (this.ticketArgs.subject || this.ticketArgs.description || this.selectedAudioSoftware || this.selectedProduct || this.selectedOperatingSystem || this.selectedCountry)) {
      this._translate.get("support.close_ticket").subscribe((lang) => {
        if (confirm(lang)) {
          this.supportService.inProgressTicketArgs = {
            subject: this.ticketArgs.subject,
            description: this.ticketArgs.description,
            selectedAudioSoftware: this.selectedAudioSoftware,
            selectedProduct: this.selectedProduct,
            selectedOperatingSystem: this.selectedOperatingSystem,
            selectedCountry: this.selectedCountry,
          };
          this.activeModal.close("cancel");
        }
      });
    } else {
      this.activeModal.close("cancel");
    }
  }

  viewTicket() {
    this._router.navigate(["/support/ticketdetail/", this.ticketResult.id]);
    this.activeModal.close({
      ticket_id: this.ticketResult.id,
    });
  }

  private updateLiveRegion(elementId: string, count: number): void {
    const element = document.getElementById(elementId);
    if (element) {
      if (count === 0) {
        element.textContent = "No results available";
      } else {
        element.textContent = `${count} results available`;
      }
    }
  }
}
